import HelpIcon from "../components/HelpIcon";
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  DatePicker,
  Divider,
  EmptyState,
  Icon,
  IndexTable,
  InlineGrid,
  InlineStack,
  Layout,
  Modal,
  Page,
  Pagination,
  Popover,
  Select,
  Text,
  TextField,
  Tooltip,
  useIndexResourceState,
} from '../components/admin-ui';
import { AlertCircleIcon, AlertTriangleIcon, CalendarIcon, InfoIcon, SearchIcon } from '../components/admin-ui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  c_cod_blacklist,
  c_cod_merge,
  d_cod_blacklist,
  q_cod_blacklists,
  q_orders,
} from "../api";
import storageUtils from "../utils/storageUtils";
import OrderTransferDialogs from "../components/OrderTransferDialogs";
import CodFulfillmentHistory from "../components/CodFulfillmentHistory";
import { orderTransferStatus } from "../api/orderTransfers";
import { useSearchParams } from 'react-router-dom';

const formatDateForQuery = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatOrderDate = (timestamp) => {
  const date = new Date(Number(timestamp) * 1000);
  if (Number.isNaN(date.getTime())) return "–";
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")} ${String(date.getUTCHours()).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")} UTC`;
};

const formatMoney = (value, currency, formatted = false) => {
  if (value === null || value === undefined || value === "") return "–";
  if (!currency) return "Currency not recorded";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "–";
  try { return new Intl.NumberFormat("en", { style: "currency", currency, currencyDisplay: "code" }).format(amount); } catch (_) { return `${value} ${currency}`; }
};

const addressText = (address = {}) => [
  address.address1 || address.address_line1,
  address.address2 || address.address_line2,
  address.city,
  address.province || address.state,
  address.zip || address.postal_code,
  address.country || address.country_code,
].filter(Boolean).join(", ") || "No address";

const itemTitle = (item = {}) => item.product_title || item.title || item.name || "Product";

const messageFromError = (error, fallback) => {
  const body = error?.response?.data || {};
  const errors = body.errors || body.data?.errors;
  const first = errors ? Object.values(errors).flat()[0] : null;
  return first || body.msg || body.message || fallback;
};

const riskBadge = (order) => {
  if (order.type !== "cod") return <Badge tone="info">Online</Badge>;
  const map = {
    low: { tone: "success", label: "Low" },
    medium: { tone: "warning", label: "Medium" },
    high: { tone: "critical", label: "High" },
  };
  const risk = map[order.risk_level] || map.low;
  return <Badge tone={risk.tone}>{risk.label} · {order.risk_score ?? 0}</Badge>;
};

const statusBadge = (status) => {
  const map = {
    paid: { tone: "success", label: "Paid" },
    pending: { tone: "attention", label: "Pending" },
    processing: { tone: "info", label: "Processing" },
    failed: { tone: "critical", label: "Failed" },
  };
  const item = map[status] || { tone: undefined, label: status || "Unknown" };
  return <Badge tone={item.tone}>{item.label}</Badge>;
};

function MetricCard({ label, value, tone, onClick }) {
  return (
    <Card>
      <BlockStack gap="200">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="p" tone="subdued">{label}</Text>
          {tone ? <Badge tone={tone}>{label}</Badge> : null}
        </InlineStack>
        <InlineStack align="space-between" blockAlign="end">
          <Text as="p" variant="heading2xl">{value ?? 0}</Text>
          {onClick ? <Button variant="plain" onClick={onClick}>View</Button> : null}
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

export default function DashBoard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const userToken = storageUtils.get("user_token");
  const [orders, setOrders] = useState([]);
  const orderRequest = useRef(0);
  const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } = useIndexResourceState(orders, { resourceIDResolver: order => order.key });
  const [transferMode, setTransferMode] = useState(null);
  useEffect(() => {
    const requested = searchParams.get('transfer');
    if (!['export', 'import', 'tasks'].includes(requested)) return;
    setTransferMode(requested);
    const next = new URLSearchParams(searchParams); next.delete('transfer');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const [transferStatus, setTransferStatus] = useState({ counts: {} });
  const [stats, setStats] = useState({ all: 0, cod: 0, merge_candidates: 0, high_risk: 0, blacklists: 0 });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [notice, setNotice] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [orderType, setOrderType] = useState("all");
  const [riskLevel, setRiskLevel] = useState("all");
  const [fulfillmentHistoryEnabled, setFulfillmentHistoryEnabled] = useState(null);
  const [fulfillmentHistoryFilter, setFulfillmentHistoryFilter] = useState("all");
  const [historyFilterNotice, setHistoryFilterNotice] = useState(null);
  const [mergeOnly, setMergeOnly] = useState(false);
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [blacklistsOpen, setBlacklistsOpen] = useState(false);
  const [blacklists, setBlacklists] = useState([]);
  const [isDatePickerOpen, setDatePickerOpen] = useState(false);
  const [selectedMonthYear, setSelectedMonthYear] = useState({
    month: new Date().getUTCMonth(),
    year: new Date().getUTCFullYear(),
  });
  const [selectedDates, setSelectedDates] = useState({
    start: new Date(new Date().setUTCDate(new Date().getUTCDate() - 7)),
    end: new Date(),
  });
  const limit = 20;
  const transferFilters = useMemo(() => ({ start_date: formatDateForQuery(selectedDates.start), end_date: formatDateForQuery(selectedDates.end), order_type: orderType, risk_level: riskLevel, merge_candidate: mergeOnly ? 1 : 0, search }), [selectedDates, orderType, riskLevel, mergeOnly, search]);
  const refreshTransferStatus = useCallback(() => {
    if (userToken) orderTransferStatus({ user_token: userToken }).then(setTransferStatus).catch(() => {});
  }, [userToken]);
  useEffect(() => { refreshTransferStatus(); }, [refreshTransferStatus]);
  useEffect(() => { clearSelection(); }, [page, transferFilters, fulfillmentHistoryFilter, clearSelection]);

  const queryOrders = useCallback(async () => {
    if (!userToken) return;
    const request = ++orderRequest.current;
    setLoading(true);
    try {
      const response = await q_orders({
        user_token: userToken,
        page,
        limit,
        start_date: formatDateForQuery(selectedDates.start),
        end_date: formatDateForQuery(selectedDates.end),
        order_type: orderType,
        risk_level: riskLevel,
        fulfillment_history_filter: fulfillmentHistoryFilter,
        merge_candidate: mergeOnly ? 1 : 0,
        search,
      });
      if (request !== orderRequest.current) return;
      const data = response.data || {};
      setFulfillmentHistoryEnabled(typeof data.fulfillment_history_enabled === 'boolean' ? data.fulfillment_history_enabled : null);
      if (fulfillmentHistoryFilter !== 'all' && (data.fulfillment_history_filter_status !== 'applied'
        || data.fulfillment_history_filter !== fulfillmentHistoryFilter)) {
        setHistoryFilterNotice(data.fulfillment_history_filter_status === 'disabled'
          ? 'Delivery history reminders are disabled. Showing orders without the delivery history filter.'
          : 'The delivery history filter is unavailable. Showing orders without this filter.');
        setFulfillmentHistoryFilter('all'); setPage(1);
      }
      setOrders(data.list || []);
      setTotalCount(response.data?.total || 0);
      setLastPage(response.data?.last_page || 1);
      setStats((current) => ({ ...current, ...(response.data?.stats || {}) }));
    } catch (error) {
      if (request === orderRequest.current && error?.code !== 'ERR_CANCELED' && !error?.__CANCEL__) {
        const errors = error?.response?.data?.errors || error?.response?.data?.data?.errors;
        if (fulfillmentHistoryFilter !== 'all' && errors?.fulfillment_history_filter) {
          setHistoryFilterNotice('The delivery history filter is unavailable. Showing orders without this filter.');
          setFulfillmentHistoryFilter('all'); setPage(1);
          return;
        }
        setNotice({ tone: "critical", message: messageFromError(error, "Unable to load orders.") });
      }
    } finally {
      if (request === orderRequest.current) setLoading(false);
    }
  }, [mergeOnly, orderType, page, riskLevel, fulfillmentHistoryFilter, search, selectedDates, userToken]);

  // Fast workers may finish before the first queued status poll. Refresh the
  // order rows when terminal counts change as well as during queued polling.
  useEffect(() => { queryOrders(); }, [queryOrders, transferStatus.counts?.synced, transferStatus.counts?.failed]);
  const syncingOrders = Number(transferStatus.counts?.queued || 0) + Number(transferStatus.counts?.processing || 0);
  useEffect(() => {
    if (!syncingOrders) return;
    const timer = setInterval(() => { refreshTransferStatus(); queryOrders(); }, 10000);
    return () => clearInterval(timer);
  }, [syncingOrders, refreshTransferStatus, queryOrders]);

  const changeFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const createBlacklist = async (order, subject) => {
    const label = subject === "ip" ? `IP ${order.ip}` : "this customer’s phone and email";
    if (!window.confirm(`Add ${label} to the COD blacklist? Future COD orders will be blocked.`)) return;
    const key = `blacklist:${subject}:${order.id}`;
    try {
      setActionLoading(key);
      setNotice(null);
      const response = await c_cod_blacklist({
        user_token: userToken,
        cod_order_id: order.id,
        subject,
      });
      setNotice({ tone: "success", message: response.msg || "Blacklist updated." });
      await queryOrders();
    } catch (error) {
      setNotice({ tone: "critical", message: messageFromError(error, "Unable to update the blacklist.") });
    } finally {
      setActionLoading("");
    }
  };

  const mergeOrders = async (order) => {
    if (!window.confirm(`Group ${order.merge_count} COD orders for combined fulfillment? The original orders remain separate records.`)) return;
    const key = `merge:${order.merge_group_id}`;
    try {
      setActionLoading(key);
      setNotice(null);
      const response = await c_cod_merge({
        user_token: userToken,
        merge_group_id: order.merge_group_id,
      });
      setNotice({ tone: "success", message: response.msg || "Orders grouped for combined fulfillment." });
      setSelectedOrder(null);
      await queryOrders();
    } catch (error) {
      setNotice({ tone: "critical", message: messageFromError(error, "Unable to group these orders.") });
    } finally {
      setActionLoading("");
    }
  };

  const openBlacklists = async () => {
    setBlacklistsOpen(true);
    setActionLoading("blacklists");
    try {
      const response = await q_cod_blacklists({ user_token: userToken });
      setBlacklists(response.data?.list || []);
    } catch (error) {
      setNotice({ tone: "critical", message: messageFromError(error, "Unable to load the blacklist.") });
    } finally {
      setActionLoading("");
    }
  };

  const removeBlacklist = async (entry) => {
    try {
      setActionLoading(`remove-blacklist:${entry.id}`);
      const response = await d_cod_blacklist({ user_token: userToken, id: entry.id });
      setBlacklists((current) => current.filter((item) => item.id !== entry.id));
      setStats((current) => ({ ...current, blacklists: Math.max(0, current.blacklists - 1) }));
      setNotice({ tone: "success", message: response.msg || "Blacklist entry removed." });
    } catch (error) {
      setNotice({ tone: "critical", message: messageFromError(error, "Unable to remove the blacklist entry.") });
    } finally {
      setActionLoading("");
    }
  };

  const rows = useMemo(() => orders.map((order, index) => (
    <IndexTable.Row id={order.key} key={order.key} position={index} selected={selectedResources.includes(order.key)}>
      <IndexTable.Cell>
        <BlockStack gap="100">
          <Text fontWeight="bold" as="span">{order.name}</Text>
          {order.source_shop ? <Text as="span" tone="subdued">Source: {order.source_shop}</Text> : null}
        </BlockStack>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <BlockStack gap="100">
          <Text as="span">{order.customer || "N/A"}</Text>
          {order.phone ? <Text as="span" tone="subdued">{order.phone}</Text> : null}
        </BlockStack>
      </IndexTable.Cell>
      <IndexTable.Cell><Text as="span">{order.ip || "—"}</Text></IndexTable.Cell>
      <IndexTable.Cell><Box maxWidth="240px"><Text as="span">{addressText(order.address)}</Text></Box></IndexTable.Cell>
      <IndexTable.Cell>
        <Box maxWidth="220px">
          <Tooltip width="wide" content={(
            <span style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>
              {(order.items || []).map(itemTitle).join(", ") || "No items"}
            </span>
          )}>
            <div tabIndex={0} style={{ maxWidth: "220px" }}>
              <Text as="p" truncate>{(order.items || []).map(itemTitle).join(", ") || "No items"}</Text>
            </div>
          </Tooltip>
        </Box>
      </IndexTable.Cell>
      <IndexTable.Cell><BlockStack gap="100"><Text as="span" fontWeight="bold">{formatMoney(order.total, order.currency)}</Text><Text as="span" tone="subdued">{order.collection_due ? "To collect" : (order.payment_confirmed || order.status === "paid") ? "Paid" : "Order amount"}</Text></BlockStack></IndexTable.Cell>
      <IndexTable.Cell><BlockStack gap="200">{riskBadge(order)}<CodFulfillmentHistory order={order} enabled={fulfillmentHistoryEnabled} /></BlockStack></IndexTable.Cell>
      <IndexTable.Cell>
        <CombineIndicator order={order} />
      </IndexTable.Cell>
      <IndexTable.Cell>{formatOrderDate(order.create_time)}</IndexTable.Cell>
      <IndexTable.Cell><BlockStack gap="100">{statusBadge(order.status)}{order.shipment?.tracking_number ? <>
        <Text as="span">{order.shipment.tracking_number}</Text><Badge tone={order.shipment.sync_status === 'synced' ? 'success' : order.shipment.sync_status === 'failed' ? 'critical' : 'attention'}>{`Shopify: ${order.shipment.sync_status}`}</Badge>
      </> : null}</BlockStack></IndexTable.Cell>
      <IndexTable.Cell><Button variant="plain" onClick={() => setSelectedOrder(order)}>View</Button></IndexTable.Cell>
    </IndexTable.Row>
  )), [orders, selectedResources, fulfillmentHistoryEnabled]);

  return (
    <Page title="Orders Management" subtitle="Online and cash-on-delivery orders in one operational view. Times are shown in UTC." fullWidth
      primaryAction={{ content: "Export orders", onAction: () => setTransferMode("export"), disabled: fulfillmentHistoryFilter !== "all" }}
      secondaryActions={[{ content: "Import tracking numbers", onAction: () => setTransferMode("import") }, { content: "Sync & transfer history", onAction: () => setTransferMode("tasks") }]}>
      <BlockStack gap="500">
        {notice ? <Banner tone={notice.tone} onDismiss={() => setNotice(null)}>{notice.message}</Banner> : null}
        {historyFilterNotice ? <Banner tone="warning" onDismiss={() => setHistoryFilterNotice(null)}>{historyFilterNotice}</Banner> : null}
        {fulfillmentHistoryFilter !== 'all' ? <Text as="p" tone="subdued">Select orders to export while a delivery history filter is applied.</Text> : null}
        {Object.entries(transferStatus.counts || {}).some(([state, count]) => state !== 'synced' && Number(count) > 0) ? <Banner tone="info" action={{ content: "Review Shopify sync tasks", onAction: () => setTransferMode("tasks") }}>
          Tracking saved locally: {transferStatus.counts.pending || 0} pending sync, {Number(transferStatus.counts.queued || 0) + Number(transferStatus.counts.processing || 0)} queued / processing, {transferStatus.counts.failed || 0} failed. Submit pending tasks to fulfill orders in Shopify.
        </Banner> : null}

        <InlineGrid columns={{ xs: 1, sm: 2, lg: 5 }} gap="300">
          <MetricCard label="All orders" value={stats.all} onClick={() => { setOrderType("all"); setRiskLevel("all"); setMergeOnly(false); setFulfillmentHistoryFilter("all"); setHistoryFilterNotice(null); setPage(1); }} />
          <MetricCard label="COD orders" value={stats.cod} tone="attention" onClick={() => { setOrderType("cod"); setMergeOnly(false); setPage(1); }} />
          <MetricCard label="Can combine" value={stats.merge_candidates} tone="warning" onClick={() => { setOrderType("cod"); setMergeOnly(true); setPage(1); }} />
          <MetricCard label="High risk" value={stats.high_risk} tone="critical" onClick={() => { setOrderType("cod"); setRiskLevel("high"); setMergeOnly(false); setPage(1); }} />
          <MetricCard label="Blacklisted" value={stats.blacklists} onClick={openBlacklists} />
        </InlineGrid>

        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineGrid columns={{ xs: 1, md: 5 }} gap="300">
                  <TextField
                    label="Search orders"
                    labelHidden
                    value={searchDraft}
                    onChange={setSearchDraft}
                    onKeyPress={(event) => { if (event.key === "Enter") { setSearch(searchDraft.trim()); setPage(1); } }}
                    prefix={<Icon source={SearchIcon} />}
                    placeholder="Order, email, phone or IP"
                    autoComplete="off"
                    connectedRight={<Button onClick={() => { setSearch(searchDraft.trim()); setPage(1); }}>Search</Button>}
                  />
                  <Select label="Order type" labelHidden options={[{ label: "All order types", value: "all" }, { label: "Online", value: "online" }, { label: "Cash on delivery", value: "cod" }]} value={orderType} onChange={(value) => { setOrderType(value); if (value === "online") { setRiskLevel("all"); setMergeOnly(false); setFulfillmentHistoryFilter("all"); setHistoryFilterNotice(null); } setPage(1); }} />
                  <Select label="Risk level" labelHidden options={[{ label: "All risk levels", value: "all" }, { label: "Low COD risk", value: "low" }, { label: "Medium COD risk", value: "medium" }, { label: "High COD risk", value: "high" }]} value={riskLevel} onChange={changeFilter(setRiskLevel)} disabled={orderType === "online"} />
                  <Select label="Delivery history" labelHidden options={[
                    { label: "All delivery histories", value: "all" },
                    { label: "Has refusal history", value: "has_refusals" },
                    { label: "Has delivery history", value: "has_deliveries" },
                    { label: "No delivery history", value: "no_history" },
                  ]} value={fulfillmentHistoryFilter} onChange={value => { setFulfillmentHistoryFilter(value); setHistoryFilterNotice(null); if (value !== 'all') setOrderType('cod'); setPage(1); }} disabled={fulfillmentHistoryEnabled !== true || orderType === "online"} />
                  <Button pressed={mergeOnly} onClick={() => { setMergeOnly(!mergeOnly); setOrderType("cod"); setPage(1); }}>Can combine only</Button>
                </InlineGrid>
                <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                  <Text as="p" tone="subdued">{totalCount} matching orders</Text>
                  <InlineStack gap="200">
                    <Popover
                      active={isDatePickerOpen}
                      activator={<Button onClick={() => setDatePickerOpen(!isDatePickerOpen)} icon={CalendarIcon}>{selectedDates.start ? `${formatDateForQuery(selectedDates.start)} – ${formatDateForQuery(selectedDates.end)}` : "All time"}</Button>}
                      onClose={() => setDatePickerOpen(false)}
                    >
                      <Box padding="400">
                        <DatePicker
                          month={selectedMonthYear.month}
                          year={selectedMonthYear.year}
                          onChange={(range) => { setSelectedDates(range); setPage(1); }}
                          onMonthChange={(month, year) => setSelectedMonthYear({ month, year })}
                          selected={selectedDates.start ? selectedDates : undefined}
                          allowRange
                          disableDatesAfter={new Date()}
                        />
                      </Box>
                    </Popover>
                    <Button variant="tertiary" onClick={() => { setSelectedDates({ start: null, end: null }); setPage(1); }}>Show all dates</Button>
                  </InlineStack>
                </InlineStack>
              </BlockStack>
            </Card>

            <Box paddingBlockStart="400">
              <Card padding="0">
                {orders.length === 0 && !loading ? (
                  <EmptyState heading="No orders match these filters" image="">
                    <p>Try another date range, order type, risk level or search.</p>
                  </EmptyState>
                ) : (
                  <IndexTable
                    resourceName={{ singular: "order", plural: "orders" }}
                    itemCount={orders.length}
                    headings={[
                      { title: "Order" },
                      { title: "Customer" },
                      { title: "IP address" },
                      { title: "Address" },
                      { title: "Items" },
                      { title: "Order amount / currency" },
                      { title: "Risk" },
                      { id: "combine", title: (
                        <InlineStack gap="150" blockAlign="center" wrap={false}>
                          <span>Combine</span>
                          <Tooltip preferredPosition="above" width="wide" content={(
                            <BlockStack gap="200">
                              <Text as="p">Match indicators compare successfully created COD orders from the same source and fulfillment stores within 24 hours of this order. Indicators do not automatically create or change fulfillment groups.</Text>
                              <Text as="p">Country, state, city, postal code, street address and apartment/unit must all match. Capitalization and extra spaces are ignored; address abbreviations are not.</Text>
                              <Text as="p">Same address: phone and email both match = Review; either one matches = High match; only recipient name matches = Verify recipient; neither matches = Address only. Empty values are not matches. Phone numbers are normalized by country.</Text>
                              <Text as="p">Before combining, confirm that none of the orders has shipped or been cancelled. These statuses are not automatically checked.</Text>
                              <Text as="p">Combining marks orders for fulfillment together. Shopify orders remain separate; shipping charges and totals are not recalculated.</Text>
                            </BlockStack>
                          )}>
                            <button type="button" aria-label="About Combine" style={{ display: "inline-flex", padding: 0, border: 0, background: "transparent", color: "#616161", cursor: "help", borderRadius: "50%" }}>
                              <HelpIcon />
                            </button>
                          </Tooltip>
                        </InlineStack>
                      ) },
                      { title: "Date (UTC)" },
                      { title: "Status" },
                      { id: "actions", title: "Actions" },
                    ]}
                    selectable
                    selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
                    onSelectionChange={handleSelectionChange}
                    promotedBulkActions={[{ content: `Export selected (${selectedResources.length})`, onAction: () => setTransferMode('export') }]}
                    loading={loading}
                  >
                    {rows}
                  </IndexTable>
                )}
                <Box padding="400">
                  <InlineStack align="center">
                    <Pagination hasPrevious={page > 1} onPrevious={() => setPage((value) => value - 1)} hasNext={page < lastPage} onNext={() => setPage((value) => value + 1)} label={`Page ${page} of ${lastPage}`} />
                  </InlineStack>
                </Box>
              </Card>
            </Box>
          </Layout.Section>
        </Layout>
      </BlockStack>

      <Modal open={Boolean(selectedOrder)} onClose={() => setSelectedOrder(null)} title={selectedOrder?.name || "Order details"}>
        <Modal.Section>
          {selectedOrder ? (
            <BlockStack gap="400">
              <InlineStack gap="200" blockAlign="center">
                <Badge tone={selectedOrder.type === "cod" ? "attention" : "info"}>{selectedOrder.type === "cod" ? "COD" : "Online"}</Badge>
                {riskBadge(selectedOrder)}
                {statusBadge(selectedOrder.status)}
              </InlineStack>
              <InlineGrid columns={{ xs: 1, sm: 2 }} gap="400">
                <BlockStack gap="100"><Text as="p" tone="subdued">Customer</Text><Text as="p">{selectedOrder.email || selectedOrder.phone || "N/A"}</Text>{selectedOrder.phone ? <Text as="p">{selectedOrder.phone}</Text> : null}</BlockStack>
                <BlockStack gap="100"><Text as="p" tone="subdued">Placed</Text><Text as="p">{formatOrderDate(selectedOrder.create_time)}</Text></BlockStack>
                <BlockStack gap="100"><Text as="p" tone="subdued">Delivery address</Text><Text as="p">{addressText(selectedOrder.address)}</Text></BlockStack>
                <BlockStack gap="100"><Text as="p" tone="subdued">IP address</Text><Text as="p">{selectedOrder.ip || "–"}</Text></BlockStack>
              </InlineGrid>
              <Text as="p" variant="headingLg">{formatMoney(selectedOrder.total, selectedOrder.currency)} · {selectedOrder.collection_due ? "To collect" : (selectedOrder.payment_confirmed || selectedOrder.status === "paid") ? "Paid" : "Order amount"}</Text>
              {selectedOrder.source_shop ? <Text as="p">Source store: {selectedOrder.source_shop}</Text> : null}
              <PriceSnapshot order={selectedOrder} />
              {selectedOrder.shipment ? <BlockStack gap="200"><Text as="h3" variant="headingSm">Shipment & tracking</Text>
                <Text as="p">{selectedOrder.shipment.reference}</Text><Text as="p">{selectedOrder.shipment.tracking_company} {selectedOrder.shipment.tracking_number || 'Tracking not imported'}</Text>
                <Text as="p">Shopify sync: {selectedOrder.shipment.sync_status}</Text>
                {selectedOrder.shipment.sync_error && <Banner tone="warning">{selectedOrder.shipment.sync_error}</Banner>}
              </BlockStack> : null}
              <Divider />
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">Items</Text>
                {(selectedOrder.items || []).map((item, index) => (
                  <InlineStack key={`${itemTitle(item)}-${index}`} align="space-between" gap="300">
                    <Text as="p">{itemTitle(item)} × {item.quantity || 1}</Text>
                    <Text as="p">{formatMoney(item.unit_price ?? item.price ?? item.variant_price, selectedOrder.currency, selectedOrder.type === "cod" || selectedOrder.price_unit === "major")}</Text>
                  </InlineStack>
                ))}
              </BlockStack>
              {selectedOrder.type === "cod" ? (
                <>
                  {selectedOrder.cod_upsell_status && <Banner tone={selectedOrder.cod_upsell_status === "completed" ? "success" : "info"}>
                    {selectedOrder.cod_upsell_status === "completed" ? "COD add-ons are included in this order total."
                      : selectedOrder.cod_upsell_status === "pending" ? "COD add-ons are being added. Refresh the order to see its updated total."
                        : "The COD add-on update could not be confirmed. Check the Shopify order before making manual changes."}
                  </Banner>}
                  <Divider />
                  <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
                    <BlockStack gap="100"><Text as="p" tone="subdued">Shipping</Text><Text as="p">{formatMoney(selectedOrder.shipping_amount, selectedOrder.currency, selectedOrder.type === "cod")}</Text></BlockStack>
                    <BlockStack gap="100"><Text as="p" tone="subdued">Discount</Text><Text as="p">{selectedOrder.discount_code ? `${selectedOrder.discount_code} · −${formatMoney(selectedOrder.discount_amount, selectedOrder.currency, selectedOrder.type === "cod")}` : "None"}</Text></BlockStack>
                    <BlockStack gap="100"><Text as="p" tone="subdued">Total</Text><Text as="p" fontWeight="bold">{formatMoney(selectedOrder.total, selectedOrder.currency, selectedOrder.type === "cod")}</Text></BlockStack>
                  </InlineGrid>
                  <CodFulfillmentHistory order={selectedOrder} enabled={fulfillmentHistoryEnabled} detailed />
                  {selectedOrder.risk_reasons?.length ? <Banner tone="warning">{selectedOrder.risk_reasons.join(" · ")}</Banner> : null}
                  {selectedOrder.combine_match ? (
                    <BlockStack gap="200">
                      <CombineIndicator order={selectedOrder} />
                      <Text as="p">{COMBINE_MATCH_LABELS[selectedOrder.combine_match.level]?.detail}</Text>
                      <Text as="p" tone="subdued">Same-address suggestions may include orders outside the existing fulfillment group. Review each order before combining.</Text>
                    </BlockStack>
                  ) : null}
                  {selectedOrder.merge_status ? (
                    <Banner tone={selectedOrder.can_manage_source !== false && selectedOrder.merge_status === "candidate" ? "info" : "success"}>
                      {selectedOrder.merge_status === "candidate" ? `${selectedOrder.merge_count} orders are in the fulfillment candidate group. Verify their contact details before combining.` : selectedOrder.merge_status === "dismissed" ? "This fulfillment candidate was dismissed." : "These orders are grouped for combined fulfillment."}
                    </Banner>
                  ) : null}
                  <InlineStack gap="200" wrap>
                    {selectedOrder.can_manage_source !== false && selectedOrder.merge_status === "candidate" ? <Button variant="primary" onClick={() => mergeOrders(selectedOrder)} loading={actionLoading === `merge:${selectedOrder.merge_group_id}`}>Combine fulfillment</Button> : null}
                    {selectedOrder.can_manage_source !== false && selectedOrder.ip ? <Button tone="critical" onClick={() => createBlacklist(selectedOrder, "ip")} loading={actionLoading === `blacklist:ip:${selectedOrder.id}`}>Blacklist IP</Button> : null}
                    {selectedOrder.can_manage_source !== false && (selectedOrder.phone || selectedOrder.email) ? <Button tone="critical" onClick={() => createBlacklist(selectedOrder, "customer")} loading={actionLoading === `blacklist:customer:${selectedOrder.id}`}>Blacklist customer</Button> : null}
                  </InlineStack>
                </>
              ) : null}
            </BlockStack>
          ) : null}
        </Modal.Section>
      </Modal>

      <Modal open={blacklistsOpen} onClose={() => setBlacklistsOpen(false)} title="COD blacklist">
        <Modal.Section>
          {actionLoading === "blacklists" ? (
            <Text as="p" tone="subdued">Loading blacklist…</Text>
          ) : blacklists.length === 0 ? (
            <Text as="p" tone="subdued">No active IP or customer blacklist entries.</Text>
          ) : (
            <BlockStack gap="300">
              {blacklists.map((entry) => (
                <Card key={entry.id} background="bg-surface-secondary">
                  <InlineStack align="space-between" blockAlign="center" gap="300" wrap={false}>
                    <BlockStack gap="100">
                      <InlineStack gap="200" blockAlign="center">
                        <Badge>{Number(entry.subject_type) === 1 ? "IP" : Number(entry.subject_type) === 2 ? "Phone" : Number(entry.subject_type) === 4 ? "Domain" : "Email"}</Badge>
                        <Text as="p" fontWeight="semibold">{entry.subject_masked || "Hidden"}</Text>
                      </InlineStack>
                      {entry.reason ? <Text as="p" tone="subdued">{entry.reason}</Text> : null}
                      <Text as="p" tone="subdued">{entry.expires_at ? `Expires ${formatOrderDate(entry.expires_at)}` : "No expiration"}</Text>
                    </BlockStack>
                    <Button variant="plain" tone="critical" onClick={() => removeBlacklist(entry)} loading={actionLoading === `remove-blacklist:${entry.id}`}>Remove</Button>
                  </InlineStack>
                </Card>
              ))}
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
      <OrderTransferDialogs mode={transferMode} onClose={() => setTransferMode(null)} userToken={userToken}
        selectedKeys={selectedResources} selectedOnly={fulfillmentHistoryFilter !== 'all'} filters={transferFilters} onStatus={setTransferStatus}
        onImported={() => { refreshTransferStatus(); queryOrders(); }} />
    </Page>
  );
}

const COMBINE_MATCH_LABELS = {
  exact: { label: "Review", tone: "attention", detail: "Phone and email both match." },
  high: { label: "High match", tone: "info", icon: InfoIcon, summary: "One contact matches · verify the other", detail: "Only phone or email matches. Verify the other contact before combining." },
  name: { label: "Verify recipient", tone: "warning", icon: AlertTriangleIcon, summary: "Same name · phone and email differ", detail: "Phone and email do not match; only recipient name matches. Confirm these orders belong to the same recipient." },
  address: { label: "Address only", tone: "critical", icon: AlertCircleIcon, summary: "Name and contacts differ · confirm first", detail: "Only the address matches. Contact details and recipient name do not match. Confirm with the customers before combining." },
};

function CombineIndicator({ order }) {
  if (order.type !== "cod") return <Badge>Not applicable</Badge>;
  const match = order.combine_match;
  const policy = COMBINE_MATCH_LABELS[match?.level];
  const closed = ["merged", "dismissed"].includes(order.merge_status);
  const label = closed ? (order.merge_status === "merged" ? "Combined" : "Dismissed")
    : policy ? policy.label + ": " + match.count + " orders"
      : order.merge_status === "candidate" ? "Review: " + order.merge_count + " orders" : "No match";
  return (
    <Tooltip width="wide" content={(
      <BlockStack gap="150">
        <Text as="p">{policy ? "Exact address match. " + policy.detail : "No additional same-address match found."}</Text>
        {(match?.matches || []).map((peer, index) => <Text as="p" key={index}>{peer.name}: {COMBINE_MATCH_LABELS[peer.level]?.label}</Text>)}
        <Text as="p">Check shipment and cancellation status. Matching suggestions do not automatically combine orders.</Text>
      </BlockStack>
    )}>
      <span tabIndex={0}>
        <BlockStack gap="100" inlineAlign="start">
          <Badge tone={closed ? (order.merge_status === "merged" ? "success" : undefined) : (policy?.tone ?? (order.merge_status === "candidate" ? "attention" : undefined))} icon={closed ? undefined : policy?.icon}>{label}</Badge>
          {closed && policy && match.level !== "exact" ? <Badge tone={policy.tone} icon={policy.icon}>{policy.label}</Badge> : null}
          {policy?.summary ? <Text as="span" variant="bodySm" tone={match.level === "address" ? "critical" : "subdued"}>{policy.summary}</Text> : null}
        </BlockStack>
      </span>
    </Tooltip>
  );
}

function PriceSnapshot({ order }) {
  const snapshot = order.price_snapshot;
  if (!snapshot) return <Text as="p" tone="subdued">Price snapshot not recorded for this order. Historical rates are not recalculated.</Text>;
  const conversion = snapshot.conversion;
  const customer = conversion ? {currency: conversion.currency, total: conversion.total, shipping: {amount: conversion.shipping_amount},
    discount: {amount: conversion.discount_amount}, upsell_subtotal: conversion.before_upsell_subtotal,
    currency_context: {base_currency: snapshot.currency, rate: conversion.rate}} : null;
  const currency = customer?.currency || snapshot.currency;
  const quote = customer || snapshot;
  return <Card><BlockStack gap="200">
    <Text as="h3" variant="headingSm">Price at order confirmation</Text>
    <Text as="p">Original total: {formatMoney(quote.total, currency)}</Text>
    {snapshot.breakdown_available === false ? <Text as="p" tone="subdued">Payment confirmed. A unique shipping and discount breakdown could not be linked to this payment.</Text> : <Text as="p">Shipping: {formatMoney(quote.shipping?.amount ?? 0, currency)} · Discount: {formatMoney(quote.discount?.amount ?? 0, currency)}</Text>}
    {customer?.currency_context ? <Text as="p">Rate at order: 1 {customer.currency_context.base_currency} = {customer.currency_context.rate} {currency}</Text> : null}
    {quote.upsell_subtotal || snapshot.before_upsell_subtotal ? <Text as="p">Before upsell included: {formatMoney(quote.upsell_subtotal ?? snapshot.before_upsell_subtotal, currency)}</Text> : null}
    {(order.price_additions || []).map((addition, index) => <Text as="p" key={index}>After upsell #{index + 1}: +{formatMoney(addition.customer_additional_amount ?? addition.additional_amount, addition.customer_currency ?? addition.currency)}</Text>)}
    <Text as="p" tone="subdued">Saved prices remain unchanged when exchange rates or promotion settings change.</Text>
  </BlockStack></Card>;
}
